import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchRatios,
  fetchBenchmarks,
  updateRatio,
  updateBenchmark,
  resetRatio,
  resetBenchmark,
} from '../../api/reference';
import type { RatioItem, BenchmarkItem } from '../../api/reference';
import { updateProject } from '../../api/projects';
import { useAuthStore } from '../../stores/authStore';
import { useEstimationStore } from '../../stores/estimationStore';
import { hasMinRole } from '../../utils/rbac';
import { RATIO_LABELS } from '../../utils/zoneRatios';
import { toast } from '../../stores/uiStore';
import { InfoTip } from '../shared/InfoTip';
import styles from './RatiosTab.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtValue(value: number, unit: string | null): string {
  if (
    unit === 'CHF' || unit === 'CHF/log' || unit === 'CHF/cl' ||
    unit === 'CHF/place' || unit === 'CHF/CO' || unit === 'CHF/equipement' || unit === 'CHF/m²'
  ) {
    return `CHF ${value.toLocaleString('fr-CH')}`;
  }
  if (unit === '%') {
    return `${(value * 100).toFixed(0)}%`;
  }
  if (value < 1 && value > 0) {
    return value.toFixed(4);
  }
  return value.toLocaleString('fr-CH');
}

// Group keys for programme ratios (core planning parameters)
const PROGRAMME_KEYS = [
  'sbp_per_logement',
  'pct_activites_sbp',
  'logements_per_ha',
  'inhabitants_per_logement',
  'pct_espaces_verts_surface',
];

// Group keys for equipment norms
const EQUIPMENT_KEYS = [
  'classes_per_logements_short',
  'classes_per_logements_long',
  'creche_places_per_logements',
  'co_per_logements',
  'sport_threshold',
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface EditState {
  key: string;
  value: string;
  source: string;
  type: 'ratio' | 'benchmark';
}

interface ConfirmDialogProps {
  name: string;
  oldValue: string;
  newValue: string;
  onApplyNew: () => void;
  onApplyRetro: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ name, oldValue, newValue, onApplyNew, onApplyRetro, onCancel }: ConfirmDialogProps) {
  const { t } = useTranslation('ratios');
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);
  return (
    <div className={styles.dialogOverlay} onClick={onCancel}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogTitle}>{t('globalChange')}</div>
        <div className={styles.dialogBody}>
          {t('confirmGlobalChange', { name, old: oldValue, new: newValue })}
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            <strong>{t('applyNewOnly')}</strong> — {t('applyNewOnlyDesc')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            <strong>{t('applyRetroactively')}</strong> — {t('applyRetroDesc')}
          </div>
        </div>
        <div className={styles.dialogActions}>
          <button className={styles.dialogBtnSecondary} onClick={onCancel}>{t('cancel')}</button>
          <button className={styles.dialogBtnSecondary} onClick={onApplyRetro}>{t('applyRetroactively')}</button>
          <button className={styles.dialogBtnPrimary} onClick={onApplyNew}>{t('applyNewOnly')}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RatiosTab() {
  const { t } = useTranslation(['ratios', 'common']);
  const { id: projectId } = useParams<{ id: string }>();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser ? hasMinRole(currentUser.role, 'tenant_admin') : false;
  const reloadTenantRatios = useEstimationStore((s) => s.reloadTenantRatios);
  const projectRatioOverrides = useEstimationStore((s) => s.projectRatioOverrides);
  const setProjectRatioOverrides = useEstimationStore((s) => s.setProjectRatioOverrides);

  const [ratios, setRatios] = useState<RatioItem[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    name: string;
    key: string;
    oldValue: string;
    newValue: string;
    type: 'ratio' | 'benchmark';
    payload: { value: number; source?: string };
  } | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([fetchRatios(), fetchBenchmarks()]);
      setRatios(r);
      setBenchmarks(b);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build lookup maps
  const ratioMap = useMemo(() => new Map(ratios.map((r) => [r.key, r])), [ratios]);
  const benchmarkMap = useMemo(() => new Map(benchmarks.map((b) => [b.key, b])), [benchmarks]);

  // Split ratios into groups
  const programmeRatios = useMemo(() => ratios.filter((r) => PROGRAMME_KEYS.includes(r.key)), [ratios]);
  const equipmentRatios = useMemo(() => ratios.filter((r) => EQUIPMENT_KEYS.includes(r.key)), [ratios]);
  const costRatios = useMemo(() => ratios.filter(
    (r) => !PROGRAMME_KEYS.includes(r.key) && !EQUIPMENT_KEYS.includes(r.key) && !r.key.startsWith('zi_'),
  ), [ratios]);
  const ziRatios = useMemo(() => ratios.filter((r) => r.key.startsWith('zi_')), [ratios]);

  // ---------------------------------------------------------------------------
  // Edit handlers
  // ---------------------------------------------------------------------------

  function startEdit(key: string, type: 'ratio' | 'benchmark') {
    const item = type === 'ratio' ? ratioMap.get(key) : benchmarkMap.get(key);
    if (!item) return;
    setEditing({
      key,
      value: String(Number(item.value)),
      source: item.source ?? '',
      type,
    });
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!editing) return;
    const newVal = parseFloat(editing.value);
    if (isNaN(newVal)) return;

    const label = RATIO_LABELS[editing.key] || editing.key.replace(/_/g, ' ');
    const item = editing.type === 'ratio' ? ratioMap.get(editing.key) : benchmarkMap.get(editing.key);
    const oldVal = item ? fmtValue(Number(item.value), item.unit) : '?';
    const newDisplay = item ? fmtValue(newVal, item.unit) : String(newVal);

    // Show confirmation dialog for global changes
    setPendingConfirm({
      name: label,
      key: editing.key,
      oldValue: oldVal,
      newValue: newDisplay,
      type: editing.type,
      payload: {
        value: newVal,
        ...(editing.source ? { source: editing.source } : {}),
      },
    });
  }

  async function doSave(retroactive: boolean) {
    if (!pendingConfirm) return;
    setSaving(true);
    try {
      if (pendingConfirm.type === 'ratio') {
        await updateRatio(pendingConfirm.key, pendingConfirm.payload);
      } else {
        await updateBenchmark(pendingConfirm.key, pendingConfirm.payload);
      }

      if (retroactive) {
        // For now, retroactive just forces a reload — snapshots would need
        // backend support to mark as stale (future enhancement).
        toast.success('Ratio updated retroactively');
      } else {
        toast.success('Ratio updated');
      }

      setEditing(null);
      setPendingConfirm(null);
      await loadData();
      await reloadTenantRatios();
    } catch (err) {
      console.error('Failed to save ratio:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset(key: string, type: 'ratio' | 'benchmark') {
    setSaving(true);
    try {
      if (type === 'ratio') {
        await resetRatio(key);
      } else {
        await resetBenchmark(key);
      }
      toast.success('Reset to default');
      await loadData();
      await reloadTenantRatios();
    } catch (err) {
      console.error('Failed to reset:', err);
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Per-project override handlers
  // ---------------------------------------------------------------------------

  async function toggleProjectOverride(key: string, enable: boolean) {
    if (!projectId) return;
    const newOverrides = { ...projectRatioOverrides };
    if (enable) {
      // Initialize override with current tenant ratio value
      const ratio = ratioMap.get(key);
      if (ratio) {
        newOverrides[key] = Number(ratio.value);
      }
    } else {
      delete newOverrides[key];
    }

    try {
      await updateProject(projectId, {
        ratio_overrides: Object.keys(newOverrides).length > 0 ? newOverrides : null,
        change_summary: enable
          ? `Added ratio override: ${key}`
          : `Removed ratio override: ${key}`,
      });
      setProjectRatioOverrides(newOverrides);
      toast.success(enable ? 'Project override added' : 'Override removed');
    } catch (err) {
      console.error('Failed to update project overrides:', err);
      toast.error('Failed to update override');
    }
  }

  async function saveProjectOverride(key: string, value: number) {
    if (!projectId) return;
    const newOverrides = { ...projectRatioOverrides, [key]: value };
    try {
      await updateProject(projectId, {
        ratio_overrides: newOverrides,
        change_summary: `Updated ratio override: ${key} = ${value}`,
      });
      setProjectRatioOverrides(newOverrides);
      toast.success('Override saved');
    } catch (err) {
      console.error('Failed to save override:', err);
      toast.error('Failed to save override');
    }
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  function renderRatioCard(item: RatioItem) {
    const label = RATIO_LABELS[item.key] || item.key.replace(/_/g, ' ');
    const isEditing = editing?.key === item.key;
    const hasOverride = projectId && item.key in projectRatioOverrides;
    const displayValue = hasOverride ? (projectRatioOverrides[item.key] ?? Number(item.value)) : Number(item.value);

    return (
      <div key={item.key} className={styles.card}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={styles.cardLabel}>
            {label}
            {item.is_custom && <span className={styles.customBadge} style={{ marginLeft: 6 }}>{t('customValue')}</span>}
            {hasOverride && <span className={styles.overrideBadge} style={{ marginLeft: 6 }}>{t('projectOverride')}</span>}
          </div>
          {item.source && <div className={styles.cardSource} title={item.source}>{item.source}</div>}
        </div>

        {isEditing && !hasOverride ? (
          <div className={styles.editRow}>
            <input
              type="number"
              step="any"
              className={styles.editInput}
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              autoFocus
            />
            <input
              type="text"
              className={styles.editInputWide}
              value={editing.source}
              onChange={(e) => setEditing({ ...editing, source: e.target.value })}
              placeholder="Source"
            />
            <button className={styles.editBtnSave} onClick={saveEdit} disabled={saving}>{t('save')}</button>
            <button className={styles.editBtnCancel} onClick={cancelEdit}>{t('cancel')}</button>
          </div>
        ) : hasOverride ? (
          <div className={styles.editRow}>
            <OverrideValueEditor
              ratioKey={item.key}
              value={projectRatioOverrides[item.key] ?? Number(item.value)}
              unit={item.unit}
              onSave={saveProjectOverride}
              onRemove={() => toggleProjectOverride(item.key, false)}
            />
          </div>
        ) : (
          <>
            <div className={styles.cardValue}>{fmtValue(displayValue, item.unit)}</div>
            <div className={styles.cardActions}>
              {editMode && isAdmin && (
                <>
                  <button className={styles.iconBtn} onClick={() => startEdit(item.key, 'ratio')} title="Edit">&#9998;</button>
                  {item.is_custom && (
                    <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => handleReset(item.key, 'ratio')} title={t('resetToDefault')}>&#8634;</button>
                  )}
                </>
              )}
              {editMode && isAdmin && projectId && !hasOverride && (
                <label className={styles.overrideToggle} title={t('overrideForProject')}>
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggleProjectOverride(item.key, true)}
                  />
                  <span style={{ fontSize: 10 }}>Override</span>
                </label>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  function renderBenchmarkRow(item: BenchmarkItem) {
    const label = item.description || item.key.replace(/_/g, ' ');
    const isEditing = editing?.key === item.key;

    if (isEditing) {
      return (
        <div key={item.key} className={styles.benchmarkRow} style={{ gridTemplateColumns: '1fr' }}>
          <div className={styles.editRow}>
            <span style={{ fontSize: 12, minWidth: 140 }}>{label}</span>
            <input
              type="number"
              step="any"
              className={styles.editInput}
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              autoFocus
            />
            <input
              type="text"
              className={styles.editInputWide}
              value={editing.source}
              onChange={(e) => setEditing({ ...editing, source: e.target.value })}
              placeholder="Source"
            />
            <button className={styles.editBtnSave} onClick={saveEdit} disabled={saving}>{t('save')}</button>
            <button className={styles.editBtnCancel} onClick={cancelEdit}>{t('cancel')}</button>
          </div>
        </div>
      );
    }

    return (
      <div key={item.key} className={styles.benchmarkRow}>
        <div className={styles.benchmarkLabel}>
          {label}
          {item.is_custom && <span className={styles.customBadge} style={{ marginLeft: 6 }}>{t('customValue')}</span>}
        </div>
        <div className={styles.benchmarkValue}>{fmtValue(Number(item.value), item.unit)}</div>
        <div className={styles.benchmarkSource} title={item.source ?? ''}>{item.source ?? ''}</div>
        {editMode && isAdmin && (
          <div className={styles.benchmarkActions}>
            <button className={styles.iconBtn} onClick={() => startEdit(item.key, 'benchmark')} title="Edit">&#9998;</button>
            {item.is_custom && (
              <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => handleReset(item.key, 'benchmark')} title={t('resetToDefault')}>&#8634;</button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={styles.tab}>
        <p style={{ color: 'var(--color-text-muted)' }}>{t('loading')}</p>
      </div>
    );
  }

  const hasData = ratios.length > 0 || benchmarks.length > 0;

  return (
    <div className={styles.tab}>
      {/* Header */}
      <div className={styles.headerRow}>
        <h2 className={styles.title}>{t('title')}</h2>
        {isAdmin && (
          <button
            className={editMode ? styles.editModeBtnActive : styles.editModeBtn}
            onClick={() => { setEditMode(!editMode); setEditing(null); }}
          >
            {editMode ? t('viewMode') : t('editMode')}
          </button>
        )}
      </div>

      {!hasData && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{t('noData')}</p>
      )}

      {/* Programme ratios — card grid */}
      {programmeRatios.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('programme')}
            <InfoTip text={t('programmeNote')} />
          </h3>
          <div className={styles.cardGrid}>
            {programmeRatios.map((r) => renderRatioCard(r))}
          </div>
        </div>
      )}

      {/* Equipment norms — card grid */}
      {equipmentRatios.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('equipements')}
            <InfoTip text={t('equipementsNote')} />
          </h3>
          <div className={styles.cardGrid}>
            {equipmentRatios.map((r) => renderRatioCard(r))}
          </div>
        </div>
      )}

      {/* Cost ratios (those not in programme/equipment groups) */}
      {costRatios.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('computed')}
            <InfoTip text={t('common:tooltips.computedRatios')} />
          </h3>
          <p className={styles.sectionNote}>{t('computedNote')}</p>
          <div className={styles.cardGrid}>
            {costRatios.map((r) => renderRatioCard(r))}
          </div>
        </div>
      )}

      {/* ZI ratios */}
      {ziRatios.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('zi')}
            <InfoTip text={t('common:tooltips.ziRatios')} />
          </h3>
          <p className={styles.sectionNote}>{t('ziNote')}</p>
          <div className={styles.cardGrid}>
            {ziRatios.map((r) => renderRatioCard(r))}
          </div>
        </div>
      )}

      {/* Cost benchmarks — row layout */}
      {benchmarks.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('benchmarks')}
            <InfoTip text={t('common:tooltips.benchmarkCosts')} />
          </h3>
          <p className={styles.sectionNote}>{t('benchmarkNote')}</p>
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 8,
            padding: '4px 0',
          }}>
            {benchmarks.map((b) => renderBenchmarkRow(b))}
          </div>
        </div>
      )}

      {/* Missing data note */}
      {!hasData && (
        <div className={styles.notePanel}>
          <strong>{t('missingData')}</strong><br />
          {t('missingDataNote')}
        </div>
      )}

      {/* Confirmation dialog */}
      {pendingConfirm && (
        <ConfirmDialog
          name={pendingConfirm.name}
          oldValue={pendingConfirm.oldValue}
          newValue={pendingConfirm.newValue}
          onApplyNew={() => doSave(false)}
          onApplyRetro={() => doSave(true)}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project override inline editor
// ---------------------------------------------------------------------------

function OverrideValueEditor({
  ratioKey,
  value,
  unit,
  onSave,
  onRemove,
}: {
  ratioKey: string;
  value: number;
  unit: string | null;
  onSave: (key: string, value: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('ratios');
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(String(value));

  useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  function handleSave() {
    const num = parseFloat(inputVal);
    if (!isNaN(num)) {
      onSave(ratioKey, num);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <>
        <input
          type="number"
          step="any"
          className={styles.editInput}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        />
        <button className={styles.editBtnSave} onClick={handleSave}>{t('save')}</button>
        <button className={styles.editBtnCancel} onClick={() => setEditing(false)}>{t('cancel')}</button>
      </>
    );
  }

  return (
    <>
      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 15, cursor: 'pointer' }} onClick={() => setEditing(true)}>
        {fmtValue(value, unit)}
      </span>
      <button className={styles.iconBtn} onClick={() => setEditing(true)} title="Edit override">&#9998;</button>
      <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={onRemove} title={t('removeOverride')}>&times;</button>
    </>
  );
}
